import { Info } from "@tamagui/lucide-icons";
import { observer } from "mobx-react-lite";
import React from "react";
import { useTranslation } from "react-i18next";
import { Pressable } from "react-native";
import { Label, XStack, YStack } from "tamagui";
import { useStores } from "../../../core/stores";
import { Permission } from "../../../permissions/types";
import { Box } from "../../../ui/Box";
import { Group } from "../../../ui/Group";
import { InfoRow, type InfoRowItemProps } from "../../../ui/InfoRow";
import { Separator } from "../../../ui/Separator";
import { Title } from "../../../ui/Title";
import { AvatarSize, UserPropsAvatar } from "../../../ui/UserAvatar";
import { useDisplayNameInfo } from "../../feed/utils";
import type {
  BusinessPlanModel,
  BusinessStaticUserInfoWithUserUid,
} from "../BusinessPlanModel";

type BusinessPlanMediaUIProps = {
  businessPlanUid: string;
};

const MembersList = observer(
  ({
    title,
    members,
    businessPlan,
  }: {
    title: string;
    members: BusinessStaticUserInfoWithUserUid[];
    businessPlan: BusinessPlanModel;
  }) => {
    const { t } = useTranslation();
    const getDisplayInfo = useDisplayNameInfo();
    if (!members.length) {
      return null;
    }

    return (
      <>
        <Title>
          {title} ({members.length})
        </Title>
        <Group>
          {members.map((userInfo) => {
            const infoRowItems: InfoRowItemProps[] = [];

            const displayInfo = getDisplayInfo(userInfo);

            if (displayInfo) {
              infoRowItems.push(displayInfo);
            }

            if (!userInfo.hasJoined) {
              infoRowItems.push({
                icon: Info,
                text: t("business:not_here_yet"),
              });
            }

            return (
              <Pressable
                style={{ width: "100%" }}
                key={userInfo.userUid}
                onPress={() => {
                  businessPlan.setShowUserInfo(userInfo.userUid);
                }}
              >
                <XStack width={"100%"} gap={"$medium"} flexWrap={"wrap"}>
                  <UserPropsAvatar
                    {...userInfo}
                    size={AvatarSize.Medium}
                    showBorder={businessPlan.checkPermission(
                      Permission.AvatarShowExtraBorder,
                      userInfo.userUid,
                    )}
                  />
                  <YStack gap={"$small"}>
                    <Label>{userInfo.displayName}</Label>
                    <InfoRow items={infoRowItems} />
                  </YStack>
                </XStack>
              </Pressable>
            );
          })}
        </Group>

        <Separator />
      </>
    );
  },
);

export const BusinessPlanUsersUI = observer(
  ({ businessPlanUid }: BusinessPlanMediaUIProps) => {
    const { businessPlansStore } = useStores();
    const { t } = useTranslation();
    const businessPlan = businessPlansStore.byId.get(businessPlanUid);

    if (!businessPlan) {
      return null;
    }

    return (
      <Box>
        <YStack gap={"$medium"}>
          <MembersList
            title={t("business:members")}
            members={businessPlan.visibleUsersSorted}
            businessPlan={businessPlan}
          />
        </YStack>
      </Box>
    );
  },
);
